import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import type { SketchSolid, SketchFace, SketchVec3 } from './types';
import { buildPrism, solidToGeometry, solidBounds, toolEpsilon } from './primitives';
import { csgUnion, csgSubtract } from './csgUtil';
import { meshToFaces, type RebuildResult } from './meshToSolid';
/**
 * Push/Pull engine — the freeform workbench's signature operation.
 *
 * A face + signed distance becomes a prism. The planar fast path builds it
 * analytically; every other case runs a tolerance-padded boolean through
 * three-bvh-csg and rebuilds the analytic shell via meshToFaces' repair
 * pipeline. The tool prism is padded by `toolEpsilon` so its base never sits
 * EXACTLY coplanar with the operand surface (the one configuration the CSG
 * kernel's 1e-10 tolerances provably break on — "Non-watertight shell"
 * rejections on ordinary pockets): carves start a hair outside the surface
 * and over-travel at the far end (a flush exit becomes a through-hole,
 * SketchUp's merge behavior), fuses sink the root into the solid.
 *
 * SketchUp shared-context semantics: the prism applies to every visible,
 * unlocked solid it crosses in the same group scope — a push carves each of
 * them, a pull fuses onto each of them — so pushing a rectangle through a
 * wall also cuts what sits behind it.
 */

export interface PushPullContext {
    /** Other scene solids the prism may cross (same group scope). */
    neighbors?: SketchSolid[];
}

export interface PushPullResult {
    ok: boolean;
    solid?: SketchSolid;
    /** Neighbors reshaped by the same prism (carved on push, fused on pull). */
    neighborUpdates?: SketchSolid[];
    reason?: string;
}

/** SketchUp shared-context pool: visible, unlocked, same group scope. */
export function pushPullNeighbors(owner: SketchSolid, candidates: SketchSolid[]): SketchSolid[] {
    return candidates.filter(
        (s) => s.id !== owner.id && s.visible && !s.locked && s.groupId === owner.groupId
    );
}

/**
 * Tolerance-padded tool prism for the boolean step. Carve (distance < 0):
 * base lifted eps OUTSIDE the surface, far end over-travels 2·eps (digs
 * through flush exits). Fuse: base sunk eps INTO the solid; the far end
 * over-travels only when it lands flush on the solid's far extent (merge,
 * SketchUp-style) — otherwise the top plane stays exact so typed VCB
 * distances are not perturbed.
 */
function buildToolPrism(
    face: SketchFace,
    origin: THREE.Vector3,
    normal: THREE.Vector3,
    distance: number,
    eps: number,
    flushBeyond: boolean
): SketchFace[] {
    const carve = distance < 0;
    // Carve: base at +eps (outside), top at distance−eps (digs past flush
    // exits). Fuse: base at −eps (embedded), top at distance (+eps more when
    // flush, so coplanar merges become overlapping unions).
    const baseOffset = carve ? eps : -eps;
    const span = carve
        ? distance - 2 * eps
        : distance + (flushBeyond ? 2 * eps : eps);
    const prism = buildPrism(
        `pp-${uuidv4()}`,
        face.outer,
        face.holes,
        origin.clone().addScaledVector(normal, baseOffset),
        normal,
        span,
        face.materialId
    );
    return prism.faces;
}

/** Farthest extent of the solid along `n` (flush-exit detection). */
function extentAlong(solid: SketchSolid, n: THREE.Vector3): number {
    let max = -Infinity;
    const p = new THREE.Vector3();
    for (const face of solid.faces) {
        for (const loop of [face.outer, ...face.holes]) {
            for (const point of loop) max = Math.max(max, p.set(...point).dot(n));
        }
    }
    return max;
}

/**
 * Apply the padded tool prism to every neighbor it touches with the SAME
 * boolean as the owner (carve on push, fuse on pull). A neighbor whose
 * boolean still fails after repair stays untouched — the owner's operation
 * is never vetoed by bystander geometry. Empty-result results (a carve that
 * consumes a neighbor whole) delete nothing here: the neighbor keeps an
 * empty face set only if the caller decides so; we skip such shells.
 */
const applyToNeighbors = (
    owner: SketchSolid,
    toolFaces: SketchFace[],
    op: (a: THREE.BufferGeometry, b: THREE.BufferGeometry) => THREE.BufferGeometry,
    neighbors: SketchSolid[]
): SketchSolid[] => {
    if (neighbors.length === 0) return [];
    const toolSolid: SketchSolid = { ...owner, faces: toolFaces };
    const toolGeo = solidToGeometry(toolSolid);
    const toolBox = solidBounds(toolSolid);
    const updates: SketchSolid[] = [];
    for (const neighbor of neighbors) {
        const bounds = solidBounds(neighbor).expandByScalar(1e-9);
        if (!bounds.intersectsBox(toolBox)) continue;
        const rebuilt = meshToFaces(op(solidToGeometry(neighbor), toolGeo));
        if (!rebuilt.ok || rebuilt.faces.length === 0) continue;
        updates.push({
            ...neighbor,
            faces: rebuilt.faces.map((f) => ({ ...f, id: uuidv4() })),
        });
    }
    return updates;
};

/**
 * Push/Pull one face of `solid` by `distance` along its (outward) normal.
 * Pure function: returns the next solid state plus any neighbor solids the
 * prism reshaped; the caller commits history once for the whole gesture.
 */
export function pushPullFace(
    solid: SketchSolid,
    faceId: string,
    distance: number,
    context?: PushPullContext
): PushPullResult {
    const face = solid.faces.find((f) => f.id === faceId);
    if (!face) return { ok: false, reason: 'Face not found.' };
    if (Math.abs(distance) < 1e-9) return { ok: false, reason: 'Zero distance.' };

    const origin = new THREE.Vector3(...face.origin);
    const normal = new THREE.Vector3(...face.normal).normalize();
    // Defense in depth: the caller passes a pre-filtered pool, but re-apply
    // the shared-context rule here so a raw list can never carve hidden,
    // locked or foreign-group solids.
    const neighbors = pushPullNeighbors(solid, context?.neighbors ?? []);

    // FAST PATH (planar group): a single freshly drawn face, or a flat group
    // of coplanar faces (a rectangle split by drawings on it), is NOT a
    // manifold solid — feeding it to CSG would trip the rebuild. Pushing
    // instead BUILDS the prism analytically and keeps the sibling flat faces
    // exactly where they are (Sketch's ring + wall result). Neighbors the
    // prism crosses still get the padded boolean (shared-context cut).
    const isPlanarGroup = solid.faces.every((f) => {
        const n = new THREE.Vector3(...f.normal).normalize();
        if (n.distanceTo(normal) > 1e-4 && n.distanceTo(normal.clone().negate()) > 1e-4) return false;
        const d = -n.dot(new THREE.Vector3(...f.origin));
        const dRef = -normal.dot(origin);
        return Math.abs(d - dRef) < 1e-4;
    });
    if (isPlanarGroup) {
        const prism = buildPrism(
            `pp-${uuidv4()}`,
            face.outer,
            face.holes,
            origin,
            normal,
            distance,
            face.materialId,
            face.loopMeta
        );
        // Sibling faces of the flat group stay; the pushed face is replaced
        // by the prism shell (base face takes over its spot, flipped).
        const siblings = solid.faces
            .filter((f) => f.id !== face.id)
            .map((f) => ({ ...f, id: f.id }));
        let neighborUpdates: SketchSolid[] = [];
        if (neighbors.length > 0) {
            const op = distance < 0 ? csgSubtract : csgUnion;
            const toolFaces = buildToolPrism(face, origin, normal, distance, toolEpsilon(solid), false);
            neighborUpdates = applyToNeighbors(solid, toolFaces, op, neighbors);
        }
        return {
            ok: true,
            solid: {
                ...solid,
                faces: [...prism.faces, ...siblings],
            },
            neighborUpdates,
        };
    }

    // Tolerance-padded boolean: outward pull fuses, inward push carves. The
    // first attempt pads by toolEpsilon; a still-broken shell retries with
    // 10× the pad before giving up (repair inside meshToFaces absorbs the
    // residual seams, so rejection is a last resort).
    const eps = toolEpsilon(solid);
    const op = distance < 0 ? csgSubtract : csgUnion;
    const farPlane = extentAlong(solid, normal);
    const flushBeyond = Math.abs(origin.dot(normal) + distance - farPlane) < eps;

    let rebuilt: RebuildResult | undefined;
    let toolFaces: SketchFace[] = [];
    for (const attemptEps of [eps, eps * 10]) {
        toolFaces = buildToolPrism(face, origin, normal, distance, attemptEps, flushBeyond);
        const solidGeo = solidToGeometry(solid);
        const toolGeo = solidToGeometry({ ...solid, faces: toolFaces });
        rebuilt = meshToFaces(op(solidGeo, toolGeo));
        if (rebuilt.ok) break;
    }
    if (!rebuilt || !rebuilt.ok) {
        return { ok: false, reason: rebuilt?.reason ?? 'Boolean failed.' };
    }

    const nextFaces = rebuilt.faces.map((f) => ({ ...f, id: uuidv4() }));
    const neighborUpdates = applyToNeighbors(solid, toolFaces, op, neighbors);

    return {
        ok: true,
        solid: {
            ...solid,
            faces: nextFaces,
        },
        neighborUpdates,
    };
}

/** Plane helper exposed for tools (push preview direction checks). */
export function faceNormalOf(face: SketchFace): THREE.Vector3 {
    return new THREE.Vector3(...face.normal).normalize();
}

/** Convenience: world points of a loop (open polyline). */
export function loopVectors(loop: SketchVec3[]): THREE.Vector3[] {
    return loop.map((p) => new THREE.Vector3(...p));
}
